import { HttpClient, HttpParams } from '@angular/common/http';
import { Inject, Injectable, PipeTransform } from '@angular/core';
import { TransactionDto } from '@dto/transactionDto';
import { Observable } from 'rxjs';
import { EntityService } from './entity.service';
import { DEDatePipe } from '../../pipes/de-date.pipe';
import { TableFilter } from '@models/tableFilter';
import { RunningTransactionDetails } from '@dto/runningTransactionDetails';
import { RunningTransactionTechnicalDto } from '@dto/runningTransactionTechnicalDto';
import { TransactionMeterValuesChartData } from '@models/TransactionMeterValuesChartData';

@Injectable({
  providedIn: 'root'
})
export class TransactionsService extends EntityService {

  public entityRoute = "transactions";

  constructor(http: HttpClient, @Inject('BASE_URL') baseUrl: string) {
    super(http, baseUrl);
  }

  getTransactions(filter: TableFilter = {}): Observable<TransactionDto[]> {
    return this.http.post<TransactionDto[]>(this.baseUrl + 'api/transactions/all', filter);
  }

  getTransactionsOfUser(userId: string): Observable<TransactionDto[]> {
    return this.http.get<TransactionDto[]>(this.baseUrl + 'api/transactions/users/' + userId);
  }

  getTransactionsOfCustomer(customerId: number, filter: TableFilter = {}): Observable<TransactionDto[]> {
    return this.http.post<TransactionDto[]>(this.baseUrl + 'api/transactions/customers/' + customerId, filter);
  }

  getLastTransactionOfChargingPoint(chargingStationId: string, connectorId: number): Observable<TransactionDto> {
    return this.http.get<TransactionDto>(this.baseUrl + 'api/chargingStations/' + chargingStationId + '/' + connectorId + '/lastTransactions');
  }

  getTransactionsOfChargingStation(chargingStationId: string): Observable<TransactionDto[]> {
    return this.http.get<TransactionDto[]>(this.baseUrl + 'api/chargingStations/' + chargingStationId + '/transactions');
  }

  getTransactionsOfLast7Days(): Observable<TransactionDto[]> {
    return this.http.get<TransactionDto[]>(this.baseUrl + 'api/transactions/lastTransactions');
  }

  getTransactionsOfLast7DaysOfCustomer(customerId: number): Observable<TransactionDto[]> {
    return this.http.get<TransactionDto[]>(this.baseUrl + 'api/transactions/lastTransactions/' + customerId);
  }

  getRunningTransactions(): Observable<TransactionDto[]> {
    return this.http.get<TransactionDto[]>(this.baseUrl + 'api/transactions/runningTransactions');
  }

  getRunningTransactionsOfCustomer(customerId: number): Observable<TransactionDto[]> {
    return this.http.get<TransactionDto[]>(this.baseUrl + 'api/transactions/runningTransactionsOfCustomer/' + customerId);
  }

  getRunningTransactionsDetail(): Observable<RunningTransactionDetails[]> {
    return this.http.get<RunningTransactionDetails[]>(this.baseUrl + 'api/transactions/runningTransactions/details');
  }

  getRunningTransactionsDetailOfCustomer(customerId: number): Observable<RunningTransactionDetails[]> {
    return this.http.get<RunningTransactionDetails[]>(this.baseUrl + 'api/transactions/runningTransactionsOfCustomer/' + customerId + '/details');
  }

  getRunningTransactionsTechnical(): Observable<RunningTransactionTechnicalDto[]> {
    return this.http.get<RunningTransactionTechnicalDto[]>(this.baseUrl + 'api/transactions/runningTransactions/technical');
  }

  GetPowerConsumptionCustom(filter?): Observable<TransactionDto[]> {
    let params = new HttpParams();
    Object.keys(filter).forEach(
      key => filter[key] && (params = params.append(key, filter[key]))
    );
    return this.http.get<TransactionDto[]>(this.baseUrl + 'api/transactions/powerconsumption-custom', { params: params });
  }

  deleteTransactionsOfChargingStation(chargingStationId: string): Observable<boolean> {
    return this.http.delete<boolean>(this.baseUrl + 'api/transactions/' + chargingStationId + '/delete');
  }

  closeTransaction(transactionId: number): Observable<boolean> {
    return this.http.get<boolean>(this.baseUrl + 'api/transactions/' + transactionId);
  }

  search(searchTerm: string): Observable<TransactionDto[]> {
    return this.http.get<TransactionDto[]>(`${this.baseUrl}api/transactions/search/${searchTerm}`);
  }

  static matches(transaction: TransactionDto, term: string, pipe?: PipeTransform) {
    var meterAmount = transaction.meterAmount / 1000;

    var datePipe = new DEDatePipe();
    var startTimestamp = datePipe.transform(transaction.startTimestamp);
    var stopTimestamp = datePipe.transform(transaction.stopTimestamp);

    return String(transaction.transactionId).includes(term.toLowerCase()) ||
      transaction.successMessage?.toLowerCase().includes(term.toLowerCase()) ||
      transaction.reason?.toLowerCase().includes(term.toLowerCase()) ||
      transaction.userFirstName?.toLowerCase().includes(term.toLowerCase()) ||
      transaction.userLastName?.toLowerCase().includes(term.toLowerCase()) ||
      transaction.rfidCardIdTag?.toLowerCase().includes(term.toLowerCase()) ||
      transaction.chargingStationName?.toLowerCase().includes(term.toLowerCase()) ||
      meterAmount?.toString().includes(term.toLowerCase()) ||
      startTimestamp?.includes(term.toLowerCase()) ||
      stopTimestamp?.includes(term.toLowerCase()) ||
      transaction.duration?.includes(term.toLowerCase());
  }

  getTransactionMeterValuesChartDatas(transactionId: number) : Observable<TransactionMeterValuesChartData[]> {
    return this.http.get<TransactionMeterValuesChartData[]>(`${this.baseUrl}api/transactions/getTransactionMeterValuesChartDatas/${transactionId}`);
  }

  getKwhOfRunningTransaction(transactionId: number) : Observable<number> {
    return this.http.get<number>(`${this.baseUrl}api/transactions/getKwhOfRunningTransaction/${transactionId}`);
  }
  
}
